import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import api from '../api/api';

export default function DeleteEquipmentModal({ equipment, onClose, onDeleted }) {
  const [usage, setUsage] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/equipment/${equipment.id}/usage`)
      .then(r => setUsage(r.data))
      .catch(() => setUsage({}));
  }, [equipment.id]);

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/equipment/${equipment.id}`);
      onDeleted();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar el equipo');
      setDeleting(false);
    }
  };

  const total = usage ? Object.values(usage).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" data-testid="delete-equipment-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Eliminar equipo</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded" data-testid="close-delete-modal">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="delete-error">{error}</div>
          )}

          <p className="text-sm text-gray-600">
            Vas a eliminar de forma permanente <span className="font-semibold text-gray-900">{equipment.code} · {equipment.name}</span>.
            Esta acción no se puede deshacer.
          </p>

          {usage === null ? (
            <p className="text-xs text-gray-400">Comprobando datos vinculados...</p>
          ) : total > 0 ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 space-y-1" data-testid="delete-usage-warning">
              <p className="font-semibold">También se eliminarán los datos asociados:</p>
              <ul className="list-disc pl-4">
                {usage.work_orders > 0 && <li>{usage.work_orders} órdenes de trabajo</li>}
                {usage.hourometer > 0 && <li>{usage.hourometer} registros de horómetro</li>}
                {usage.alerts > 0 && <li>{usage.alerts} alertas</li>}
                {usage.activity > 0 && <li>{usage.activity} registros de actividad</li>}
              </ul>
              <p>Si solo quieres retirarlo de servicio, edítalo y marca el estado como <strong>Inactivo</strong>.</p>
            </div>
          ) : (
            <p className="text-xs text-gray-500" data-testid="delete-usage-empty">Este equipo no tiene datos vinculados.</p>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Escribe <span className="font-mono font-bold">{equipment.code}</span> para confirmar
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none font-mono"
              data-testid="delete-confirm-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="cancel-delete-btn">Cancelar</button>
            <button
              onClick={handleDelete}
              disabled={deleting || confirmText.trim().toUpperCase() !== (equipment.code || '').toUpperCase()}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="confirm-delete-btn"
            >
              {deleting && <Loader2 size={14} className="animate-spin" />}
              {deleting ? 'Eliminando...' : 'Eliminar definitivamente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
